import { ArrowUpRight } from "lucide-react";

const products = [
    {
        key: "expatssphere",
        name: "ExpatsSphere",
        tagline: "Find your people. Anywhere.",
        description:
            "A global networking platform for expatriates — discover events, meet fellow expats, and build meaningful communities wherever life takes you.",
        url: "https://expatssphere.com",
        logo: "/expatssphere-logo.jpg",
        logoLayout: "horizontal",
        accent: "#7C3AED",
        accentSoft: "#F4F1FF",
        platform: "Web · iOS · Android",
    },
    {
        key: "vitala",
        name: "Vitala",
        tagline: "Smart supplements. Zero guesswork.",
        description:
            "AI-powered personalized supplement recommendations with built-in drug-interaction checking — evidence-based wellness guidance, tailored to your health profile.",
        url: "https://vitala.me",
        logo: "https://vitala.me/logo512.png",
        logoLayout: "square",
        accent: "#4A6C58",
        accentSoft: "#EAF1EC",
        platform: "Web · Android",
    },
];

export const Products = () => {
    return (
        <section
            id="products"
            data-testid="products-section"
            className="py-24 md:py-36"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid grid-cols-12 gap-8 items-end mb-16">
                    <div className="col-span-12 md:col-span-7 min-w-0">
                        <p className="overline text-[var(--slate)]">
                            Our Products
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl mt-6 text-[var(--ink)] leading-[1.05] break-words">
                            Two apps, published by
                            <span className="italic text-[var(--ink-line)]">
                                {" "}
                                Medpax, LLC.
                            </span>
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-4 md:col-start-9 min-w-0 text-[var(--slate)] text-base leading-relaxed">
                        Each product is designed, developed, and maintained
                        in-house. Links below take you to the live,
                        publicly-accessible websites.
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                    {products.map(
                        ({
                            key,
                            name,
                            tagline,
                            description,
                            url,
                            logo,
                            logoLayout,
                            accent,
                            accentSoft,
                            platform,
                        }) => (
                            <a
                                key={key}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`product-card-${key}`}
                                className="product-card group block border border-[var(--line)] bg-white overflow-hidden"
                                style={{
                                    "--brand": accent,
                                    "--brand-soft": accentSoft,
                                }}
                            >
                                <div
                                    className="relative aspect-[16/10] overflow-hidden flex items-center justify-center"
                                    style={{ backgroundColor: accentSoft }}
                                >
                                    <div
                                        className="absolute inset-0 opacity-40"
                                        style={{
                                            background: `radial-gradient(circle at 70% 30%, ${accent}22, transparent 60%), radial-gradient(circle at 20% 80%, ${accent}18, transparent 55%)`,
                                        }}
                                    />
                                    {logoLayout === "horizontal" ? (
                                        <img
                                            src={logo}
                                            alt={`${name} logo`}
                                            className="relative w-[88%] max-w-[420px] h-auto object-contain drop-shadow-[0_12px_30px_rgba(10,25,47,0.08)] transition-transform duration-700 group-hover:scale-[1.03]"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <img
                                            src={logo}
                                            alt={`${name} logo`}
                                            className="relative h-28 w-28 md:h-32 md:w-32 rounded-3xl shadow-[0_20px_50px_rgba(10,25,47,0.15)] transition-transform duration-700 group-hover:scale-105 group-hover:-rotate-2"
                                            loading="lazy"
                                        />
                                    )}
                                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-white/90 backdrop-blur px-3 py-1.5 text-xs text-[var(--ink)]">
                                        <span
                                            className="inline-block h-2 w-2 rounded-full"
                                            style={{ backgroundColor: accent }}
                                        />
                                        {platform}
                                    </div>
                                </div>
                                <div className="p-7 md:p-9">
                                    <div className="flex items-start justify-between gap-6">
                                        <div>
                                            <h3 className="font-display text-3xl md:text-4xl text-[var(--ink)] leading-tight">
                                                {name}
                                            </h3>
                                            <p
                                                className="font-display italic text-lg mt-1"
                                                style={{ color: accent }}
                                            >
                                                {tagline}
                                            </p>
                                        </div>
                                        <div
                                            className="h-10 w-10 rounded-full border border-[var(--line)] flex items-center justify-center text-[var(--ink)] transition-all group-hover:text-white group-hover:border-transparent"
                                            style={{
                                                "--hover-bg": accent,
                                            }}
                                            onMouseEnter={(e) =>
                                                (e.currentTarget.style.backgroundColor =
                                                    accent)
                                            }
                                            onMouseLeave={(e) =>
                                                (e.currentTarget.style.backgroundColor =
                                                    "")
                                            }
                                        >
                                            <ArrowUpRight className="h-4 w-4" />
                                        </div>
                                    </div>
                                    <p className="mt-5 text-[var(--slate)] leading-relaxed">
                                        {description}
                                    </p>
                                    <p className="mt-6 overline text-[var(--slate)]">
                                        Visit{" "}
                                        <span className="text-[var(--ink)]">
                                            {url.replace("https://", "")}
                                        </span>
                                    </p>
                                </div>
                            </a>
                        )
                    )}
                </div>
            </div>
        </section>
    );
};
